import React from 'react'
import { Formik } from 'formik'
import { Form, Button, Link, Flex, Input } from '@sketch/components'
import Panel from '../Panel'
import * as yup from 'yup'
import {
  FormWrapper,
  Description,
  DescriptionText,
  EditButton,
  FormActions,
} from './WorkspaceSettingsEncryptionPanel.styles'
import { useConfigureWorkspaceEncryptionKeysMutation } from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { isApolloError } from '@apollo/client'
import { castError } from '@sketch/utils'

interface WorkspaceSettingsEncryptionPanelProps {
  workspaceId: string
  encryptionKeys?: string[]
  roleArn?: string
}

type Values = {
  encryptionKey: string
  roleArn: string
}

const schema = yup.object().shape({
  encryptionKey: yup.string().trim().required('Enter a valid AWS KMS ARN'),
  roleArn: yup.string().trim(),
})

export const WorkspaceSettingsEncryptionPanel: React.FC<
  WorkspaceSettingsEncryptionPanelProps
> = ({ workspaceId, encryptionKeys, roleArn }) => {
  const { showToast } = useToast()
  const [isEditingForm, setIsEditingForm] = React.useState(false)

  const [updateEncryptionSettings] =
    useConfigureWorkspaceEncryptionKeysMutation({
      onError: e => {
        const error = castError(e)
        if (isApolloError(error)) {
          const errorMessage = error.graphQLErrors
            .map(({ message }) => message)
            .join('; ')
          showToast(errorMessage, 'negative')
        } else {
          showToast('Something went wrong. Please try again later.', 'negative')
        }
      },
      onCompleted: () => {
        showToast('Configuration successfully saved', 'positive')
        setIsEditingForm(false)
      },
    })

  const onSubmit = async (value: Values) => {
    await updateEncryptionSettings({
      variables: {
        encryptionParams: {
          workspaceIdentifier: workspaceId,
          roleArn: value.roleArn === '' ? null : value.roleArn,
          kmsArns: value.encryptionKey.split(',').map(s => s.trim()),
        },
      },
    })
  }

  const initialValues: Values = {
    encryptionKey: encryptionKeys?.join(', ') ?? '',
    roleArn: roleArn ?? '',
  }

  const editRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    const element = editRef.current
    if (element !== null && isEditingForm) {
      element.focus()
    }
  }, [isEditingForm])

  return (
    <Panel>
      <Panel.Title>Set Up Encryption</Panel.Title>
      <Description>
        <DescriptionText>
          Configure the encryption keys and specify the AWS role to active
          encryption.{' '}
          <Link external href="https://sketch.com">
            Learn More
          </Link>
        </DescriptionText>
        <EditButton
          onClick={() => {
            setIsEditingForm(true)
          }}
        >
          Edit...
        </EditButton>
      </Description>

      <Panel.Body>
        <FormWrapper>
          <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={schema}
          >
            {formikBag => (
              <Form>
                <Flex flexDirection="column">
                  <Form.Field
                    name="encryptionKey"
                    label="Encryption key"
                    errorText={
                      formikBag.touched.encryptionKey
                        ? formikBag.errors.encryptionKey
                        : undefined
                    }
                  >
                    <Input
                      ref={editRef}
                      name="encryptionKey"
                      type="text"
                      aria-label="Encryption key"
                      placeholder="arn:aws:kms:::key/..."
                      value={formikBag.values.encryptionKey}
                      onChange={formikBag.handleChange}
                      onBlur={formikBag.handleBlur}
                      disabled={!isEditingForm}
                    />
                  </Form.Field>

                  <Form.Field
                    name="roleArn"
                    label="AWS Role ARN"
                    errorText={
                      formikBag.touched.roleArn
                        ? formikBag.errors.roleArn
                        : undefined
                    }
                  >
                    <Input
                      name="roleArn"
                      aria-label="AWS Role ARN"
                      type="text"
                      value={formikBag.values.roleArn}
                      placeholder="arn:aws:iam:::role/..."
                      onChange={formikBag.handleChange}
                      onBlur={formikBag.handleBlur}
                      disabled={!isEditingForm}
                    />
                  </Form.Field>

                  {isEditingForm && (
                    <FormActions>
                      <Button
                        type="button"
                        disabled={formikBag.isSubmitting}
                        size="40"
                        onClick={e => {
                          formikBag.handleReset(e)
                          setIsEditingForm(false)
                        }}
                      >
                        Cancel
                      </Button>

                      <Button
                        type="submit"
                        variant="primary"
                        size="40"
                        disabled={formikBag.isSubmitting}
                        loading={formikBag.isSubmitting}
                      >
                        Save Changes
                      </Button>
                    </FormActions>
                  )}
                </Flex>
              </Form>
            )}
          </Formik>
        </FormWrapper>
      </Panel.Body>
    </Panel>
  )
}
